<template>
  <div class="intake-print-card">
    <!-- HEADER (mirrors PatientIntakeFormPage.vue) -->
    <div class="intake-print-header" :style="{ backgroundColor: themeColor }">
      <div class="header-brand">
        <img :src="logo" alt="Clinic logo" class="clinic-logo" crossorigin="anonymous" />
        <div>
          <h2 class="intake-title">Patient Dental Intake Form</h2>
          <p class="intake-subtitle">
            Please complete all sections before your appointment. All
            information is kept strictly confidential in accordance with
            Ontario privacy legislation.
          </p>
        </div>
      </div>

      <div class="header-divider"></div>

      <div class="header-meta">
        <div class="meta-item">
          <span class="meta-label">Form date</span>
          <span class="meta-value">{{ formattedFormDate || "—" }}</span>
        </div>
      </div>
    </div>

    <div class="intake-print-body">
      <!-- PERSONAL INFORMATION -->
      <section class="form-section">
        <h3 class="section-title">Personal Information</h3>
        <div class="section-divider"></div>
        <div class="field-grid cols-3">
          <div v-for="f in personalInfoDisplay" :key="f.label" class="field-box">
            <span class="field-label">{{ f.label }}</span>
            <div class="field-value">{{ f.value || "—" }}</div>
          </div>
        </div>
      </section>

      <!-- INSURANCE & BILLING -->
      <section class="form-section">
        <h3 class="section-title">Insurance &amp; Billing</h3>
        <div class="section-divider"></div>
        <div class="field-grid cols-3">
          <div v-for="f in insuranceDisplay" :key="f.label" class="field-box">
            <span class="field-label">{{ f.label }}</span>
            <div class="field-value">{{ f.value || "—" }}</div>
          </div>
        </div>
        <div class="keep-group">
          <p class="radio-question">Is the patient covered under OHIP+ (24 and under)?</p>
          <div class="option-row">
            <span v-for="opt in ohipPlusOptions" :key="opt.value" class="option-chip">
              {{ form.ohipPlusCovered === opt.value ? "☑" : "☐" }} {{ opt.label }}
            </span>
          </div>
        </div>
      </section>

      <!-- DENTAL HISTORY -->
      <section class="form-section">
        <h3 class="section-title">Dental History</h3>
        <div class="section-divider"></div>
        <div class="field-grid cols-3">
          <div v-for="f in dentalHistoryDisplay" :key="f.label" class="field-box">
            <span class="field-label">{{ f.label }}</span>
            <div class="field-value">{{ f.value || "—" }}</div>
          </div>
        </div>

        <div class="keep-group">
          <p class="radio-question">Reason for today's visit (check all that apply)</p>
          <div class="option-row">
            <span
              v-for="opt in visitReasonOptions"
              :key="opt.value"
              class="option-chip"
              :class="{ 'option-chip--checked': isChecked(form.visitReasons, opt.value) }"
            >
              {{ isChecked(form.visitReasons, opt.value) ? "☑" : "☐" }} {{ opt.label }}
            </span>
          </div>
        </div>

        <div class="field-box field-box--full">
          <span class="field-label">Current dental concerns or pain — describe</span>
          <div class="field-value field-value--tall">{{ form.dentalConcerns || "—" }}</div>
        </div>

        <div class="keep-group">
          <p class="radio-question">Previous dental treatments received (check all that apply)</p>
          <div class="option-row">
            <span
              v-for="opt in previousTreatmentOptions"
              :key="opt.value"
              class="option-chip"
              :class="{ 'option-chip--checked': isChecked(form.previousTreatments, opt.value) }"
            >
              {{ isChecked(form.previousTreatments, opt.value) ? "☑" : "☐" }} {{ opt.label }}
            </span>
          </div>
        </div>

        <div class="keep-group">
          <p class="radio-question">Current oral hygiene routine</p>
          <div class="option-row option-row--stacked">
            <span v-for="opt in hygieneRoutineOptions" :key="opt.value" class="option-chip">
              {{ form.hygieneRoutine === opt.value ? "☑" : "☐" }} {{ opt.label }}
            </span>
          </div>
        </div>
      </section>

      <!-- MEDICAL HISTORY -->
      <section class="form-section">
        <h3 class="section-title">Medical History</h3>
        <div class="section-divider"></div>
        <div class="field-grid cols-3">
          <div v-for="f in medicalHistoryDisplay" :key="f.label" class="field-box">
            <span class="field-label">{{ f.label }}</span>
            <div class="field-value">{{ f.value || "—" }}</div>
          </div>
        </div>

        <div class="keep-group">
          <p class="radio-question">Do you have or have you had any of the following? (check all that apply)</p>
          <div class="option-row">
            <span
              v-for="opt in medicalConditionOptions"
              :key="opt.value"
              class="option-chip"
              :class="{ 'option-chip--checked': isChecked(form.medicalConditions, opt.value) }"
            >
              {{ isChecked(form.medicalConditions, opt.value) ? "☑" : "☐" }} {{ opt.label }}
            </span>
          </div>
        </div>

        <div class="keep-group">
          <p class="radio-question">Are you currently taking medications, supplements, or herbal remedies?</p>
          <div class="option-row">
            <span v-for="opt in yesNoOptions" :key="opt.value" class="option-chip">
              {{ form.takingMedications === opt.value ? "☑" : "☐" }} {{ opt.label }}
            </span>
          </div>
        </div>
        <div class="field-box field-box--full">
          <span class="field-label">List medications, dosages, and supplements</span>
          <div class="field-value field-value--tall">{{ form.medicationsList || "—" }}</div>
        </div>

        <div class="keep-group">
          <p class="radio-question">Do you have any known allergies? (check all that apply)</p>
          <div class="option-row">
            <span
              v-for="opt in allergyOptions"
              :key="opt.value"
              class="option-chip"
              :class="{ 'option-chip--checked': isChecked(form.allergies, opt.value) }"
            >
              {{ isChecked(form.allergies, opt.value) ? "☑" : "☐" }} {{ opt.label }}
            </span>
          </div>
        </div>

        <div class="field-box field-box--full">
          <span class="field-label">Other allergies or adverse drug reactions — describe</span>
          <div class="field-value">{{ form.otherAllergies || "—" }}</div>
        </div>

        <div class="field-grid cols-2 keep-group">
          <div class="field-box">
            <span class="field-label">Tobacco / smoking</span>
            <div class="option-row option-row--inline">
              <span v-for="opt in smokingOptions" :key="opt.value" class="option-chip">
                {{ form.smoking === opt.value ? "☑" : "☐" }} {{ opt.label }}
              </span>
            </div>
          </div>
          <div class="field-box">
            <span class="field-label">Alcohol use</span>
            <div class="option-row option-row--inline">
              <span v-for="opt in alcoholOptions" :key="opt.value" class="option-chip">
                {{ form.alcohol === opt.value ? "☑" : "☐" }} {{ opt.label }}
              </span>
            </div>
          </div>
        </div>

        <div class="field-box field-box--full">
          <span class="field-label">Anything else we should know (anxiety, accessibility needs, interpreter request, etc.)</span>
          <div class="field-value field-value--tall">{{ form.otherMedicalNotes || "—" }}</div>
        </div>
      </section>

      <!-- CONSENT & AUTHORIZATION -->
      <section class="form-section">
        <h3 class="section-title">Consent &amp; Authorization</h3>
        <div class="section-divider"></div>

        <div class="consent-box">
          <p>
            By signing this form, you acknowledge and agree to the following,
            in accordance with the Regulated Health Professions Act, 1991
            (Ontario), the Personal Health Information Protection Act
            (PHIPA), 2004, and the Royal College of Dental Surgeons of
            Ontario (RCDSO) guidelines.
          </p>
          <p>
            Your personal health information is collected for the purpose of
            providing dental care, processing insurance claims, and
            contacting you regarding your appointments. It will not be
            shared with third parties without your explicit consent, except
            as required by law. Treatment consent is obtained separately
            prior to each clinical procedure.
          </p>
        </div>

        <div class="consent-line">
          {{ form.consentExam ? "☑" : "☐" }} I authorize the dental team to perform an examination and any
          emergency care deemed necessary to protect my health and safety.
        </div>
        <div class="consent-line">
          {{ form.consentInsurance ? "☑" : "☐" }} I authorize the release of my dental records and health
          information to my insurance carrier(s) for the purpose of processing benefit claims.
        </div>
        <div class="consent-line">
          {{ form.consentPhipa ? "☑" : "☐" }} I have reviewed the clinic's Privacy Policy and consent to the
          collection, use, and disclosure of my personal health information as described above (PHIPA).
        </div>
        <div class="consent-line">
          {{ form.consentReminders ? "☑" : "☐" }} I consent to receive appointment reminders and recall notices
          by phone, text, or email using the contact details provided above. (Optional)
        </div>

        <div class="field-grid cols-2" style="margin-top: 14px">
          <div class="field-box">
            <span class="field-label">Patient signature (guardian signature if patient is under 16)</span>
            <div class="field-value">{{ form.patientSignature || "—" }}</div>
          </div>
          <div class="field-box">
            <span class="field-label">Signature date</span>
            <div class="field-value">{{ formatDate(form.signatureDate) || "—" }}</div>
          </div>
          <div class="field-box">
            <span class="field-label">Guardian / authorized representative full legal name</span>
            <div class="field-value">{{ form.guardianName || "—" }}</div>
          </div>
          <div class="field-box">
            <span class="field-label">Relationship to patient</span>
            <div class="field-value">{{ form.guardianRelation || "—" }}</div>
          </div>
        </div>
      </section>
    </div>

    <footer class="powered-by-footer" :style="{ backgroundColor: themeColor }">
      <p>Powered by MamaBots</p>
    </footer>
  </div>
</template>

<script>
import defaultLogo from "@/assets/img/dental-logo.png";

export default {
  name: "IntakeFormPrintTemplate",
  props: {
    formData: {
      type: Object,
      default: () => ({}),
    },
    submittedAt: {
      type: String,
      default: "",
    },
    themeColor: {
      type: String,
      default: "#0144a6",
    },
    logo: {
      type: [String, Object],
      default: () => defaultLogo,
    },
  },
  data() {
    return {
      provinces: [
        { text: "Ontario", value: "ON" },
        { text: "Alberta", value: "AB" },
        { text: "British Columbia", value: "BC" },
        { text: "Manitoba", value: "MB" },
        { text: "New Brunswick", value: "NB" },
        { text: "Newfoundland & Labrador", value: "NL" },
        { text: "Nova Scotia", value: "NS" },
        { text: "Prince Edward Island", value: "PE" },
        { text: "Quebec", value: "QC" },
        { text: "Saskatchewan", value: "SK" },
      ],
      ohipPlusOptions: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
        { label: "Not sure", value: "not_sure" },
      ],
      visitReasonOptions: [
        { label: "Routine checkup", value: "checkup" },
        { label: "Cleaning", value: "cleaning" },
        { label: "Toothache / pain", value: "pain" },
        { label: "Broken tooth", value: "broken" },
        { label: "Sensitivity", value: "sensitivity" },
        { label: "Cosmetic concern", value: "cosmetic" },
        { label: "Second opinion", value: "opinion" },
        { label: "Orthodontic consultation", value: "ortho" },
        { label: "Dental emergency", value: "emergency" },
      ],
      previousTreatmentOptions: [
        { label: "Fillings", value: "fillings" },
        { label: "Root canal", value: "rct" },
        { label: "Extraction", value: "extraction" },
        { label: "Dentures / partials", value: "dentures" },
        { label: "Orthodontics / braces", value: "ortho" },
        { label: "Implants", value: "implants" },
        { label: "Crown / bridge", value: "crown" },
        { label: "Teeth whitening", value: "whitening" },
        { label: "Veneers", value: "veneers" },
        { label: "None", value: "none" },
      ],
      hygieneRoutineOptions: [
        { label: "Brush 2× daily + floss", value: "brush2_floss" },
        { label: "Brush 2× daily only", value: "brush2" },
        { label: "Brush once daily", value: "brush1" },
        { label: "Irregular", value: "irregular" },
      ],
      medicalConditionOptions: [
        { label: "Heart disease / angina", value: "heart" },
        { label: "High / low blood pressure", value: "bp" },
        { label: "Diabetes (Type 1 or 2)", value: "diabetes" },
        { label: "Asthma / respiratory", value: "asthma" },
        { label: "Stroke / TIA", value: "stroke" },
        { label: "Epilepsy / seizures", value: "epilepsy" },
        { label: "Kidney disease", value: "kidney" },
        { label: "Liver disease / hepatitis", value: "liver" },
        { label: "HIV / AIDS", value: "hiv" },
        { label: "Cancer / chemotherapy", value: "cancer" },
        { label: "Osteoporosis / bisphosphonates", value: "osteo" },
        { label: "Artificial heart valve / pacemaker", value: "valve" },
        { label: "Joint replacement (hip, knee)", value: "joint" },
        { label: "Bleeding / clotting disorder", value: "bleeding" },
        { label: "Thyroid condition", value: "thyroid" },
        { label: "Mental health condition", value: "mental" },
        { label: "Pregnancy", value: "pregnancy" },
        { label: "Nursing / breastfeeding", value: "nursing" },
        { label: "None of the above", value: "none" },
      ],
      allergyOptions: [
        { label: "Penicillin / antibiotics", value: "penicillin" },
        { label: "Latex", value: "latex" },
        { label: "Aspirin / NSAIDs", value: "aspirin" },
        { label: "Codeine / opioids", value: "codeine" },
        { label: "Local anesthetic", value: "anesthetic" },
        { label: "Sulfa drugs", value: "sulfa" },
        { label: "None known", value: "none" },
      ],
      yesNoOptions: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
      smokingOptions: [
        { label: "Never", value: "never" },
        { label: "Former", value: "former" },
        { label: "Current", value: "current" },
      ],
      alcoholOptions: [
        { label: "Never", value: "never" },
        { label: "Occasional", value: "occasional" },
        { label: "Regular", value: "regular" },
      ],
    };
  },
  computed: {
    form() {
      return this.formData || {};
    },
    formattedFormDate() {
      const d = this.submittedAt ? new Date(this.submittedAt) : new Date();
      if (isNaN(d)) return "";
      return d.toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
    },
    provinceLabel() {
      const found = this.provinces.find((p) => p.value === this.form.province);
      return found ? found.text : this.form.province;
    },
    personalInfoDisplay() {
      return [
        { label: "Last name", value: this.form.lastName },
        { label: "First name", value: this.form.firstName },
        { label: "Preferred name", value: this.form.preferredName },
        { label: "Date of birth", value: this.formatDate(this.form.dob) },
        { label: "Sex assigned at birth", value: this.form.sexAtBirth },
        { label: "Gender identity", value: this.form.genderIdentity },
        { label: "Home phone", value: this.form.homePhone },
        { label: "Cell phone", value: this.form.cellPhone },
        { label: "Email", value: this.form.email },
        { label: "Street address", value: this.form.streetAddress },
        { label: "City", value: this.form.city },
        { label: "Province", value: this.provinceLabel },
        { label: "Postal code", value: this.form.postalCode },
        { label: "Preferred language", value: this.form.preferredLanguage },
        { label: "Emergency contact name", value: this.form.emergencyContactName },
        { label: "Emergency contact phone", value: this.form.emergencyContactPhone },
      ];
    },
    insuranceDisplay() {
      return [
        { label: "OHIP version code", value: this.form.ohipVersionCode },
        { label: "OHIP number", value: this.form.ohipNumber },
        { label: "Primary insurance carrier", value: this.form.primaryInsuranceCarrier },
        { label: "Policy / group number", value: this.form.policyGroupNumber },
        { label: "Subscriber name", value: this.form.subscriberName },
        { label: "Subscriber date of birth", value: this.formatDate(this.form.subscriberDob) },
        { label: "Secondary insurance", value: this.form.secondaryInsurance },
      ];
    },
    dentalHistoryDisplay() {
      return [
        { label: "Date of last dental visit", value: this.formatDate(this.form.lastDentalVisit) },
        { label: "Previous dentist / clinic name", value: this.form.previousDentist },
      ];
    },
    medicalHistoryDisplay() {
      return [
        { label: "Family physician name", value: this.form.physicianName },
        { label: "Physician phone", value: this.form.physicianPhone },
      ];
    },
  },
  methods: {
    isChecked(list, value) {
      return Array.isArray(list) && list.includes(value);
    },
    formatDate(value) {
      if (!value) return "";
      const parts = String(value).split("-").map(Number);
      if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return value;
      const [y, m, d] = parts;
      const date = new Date(y, m - 1, d);
      if (isNaN(date)) return value;
      return date.toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
    },
  },
};
</script>

<style scoped>
/* Signals to html2pdf's "css"/"avoid-all" pagebreak modes not to slice a
   page break through the middle of these groups. */
.field-box,
.keep-group,
.consent-box,
.consent-line {
  page-break-inside: avoid;
  break-inside: avoid;
}

.intake-print-card {
  width: 794px;
  background: #ffffff;
  font-family: Arial, Helvetica, sans-serif;
  color: #24324a;
}

.intake-print-header {
  padding: 22px 26px 18px;
  color: white;
}

.header-brand {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.clinic-logo {
  height: 44px;
  width: auto;
  max-width: 140px;
  object-fit: contain;
  margin-top: 2px;
  flex-shrink: 0;
}

.intake-title {
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 700;
}

.intake-subtitle {
  margin: 0;
  max-width: 600px;
  font-size: 13px;
  line-height: 1.5;
  opacity: 0.92;
}

.header-divider {
  border-top: 1px solid rgba(255, 255, 255, 0.25);
  margin: 18px 0 14px;
}

.header-meta {
  display: flex;
  gap: 16px;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.meta-label {
  font-size: 11px;
  opacity: 0.8;
}

.meta-value {
  font-size: 13px;
  font-weight: 600;
  border-bottom: 1px solid rgba(255, 255, 255, 0.4);
  padding-bottom: 4px;
}

.intake-print-body {
  padding: 10px 26px 20px;
}

.form-section {
  margin: 20px 0;
}

.section-title {
  font-size: 15px;
  font-weight: 700;
  color: #24324a;
  margin: 0 0 10px;
}

.section-divider {
  border-top: 1px solid #e0dfd8;
  margin-bottom: 16px;
}

.field-grid {
  display: grid;
  gap: 10px 14px;
  margin-bottom: 10px;
}

.field-grid.cols-3 {
  grid-template-columns: repeat(3, 1fr);
}

.field-grid.cols-2 {
  grid-template-columns: repeat(2, 1fr);
}

.field-box {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-box--full {
  grid-column: 1 / -1;
  margin-bottom: 10px;
}

.field-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b6b67;
}

.field-value {
  font-size: 13px;
  border: 1px solid #d8d8d3;
  border-radius: 6px;
  padding: 6px 10px;
  min-height: 16px;
  background: #fbfbfa;
  word-break: break-word;
}

.field-value--tall {
  min-height: 40px;
}

.radio-question {
  font-size: 13px;
  font-weight: 600;
  color: #24324a;
  margin: 10px 0 6px;
}

.option-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  margin-bottom: 10px;
}

.option-row--stacked {
  flex-direction: column;
  gap: 4px;
}

.option-row--inline {
  gap: 4px 14px;
}

.option-chip {
  font-size: 12.5px;
  white-space: nowrap;
}

.consent-box {
  background: #f7f7f5;
  border: 1px solid #e0dfd8;
  border-radius: 8px;
  padding: 14px 16px;
  font-size: 12px;
  color: #6b6b67;
  line-height: 1.6;
  margin-bottom: 14px;
}

.consent-box p + p {
  margin-top: 8px;
}

.consent-line {
  font-size: 12.5px;
  line-height: 1.5;
  margin-bottom: 8px;
}

.powered-by-footer {
  padding: 10px 0;
  text-align: center;
  color: white;
}

.powered-by-footer p {
  margin: 0;
  font-size: 13px;
}
</style>
